/**
 * Order status constants and configuration
 *
 * Status flow: import -> parked -> pending -> delivered
 *                   ^      |          ^
 *                   |      +----------+ (via "Fullfør parkert ordre")
 *                   +-----+ (via "Ny parkering" - re-park with locked lines)
 */

export const ORDER_STATUSES = {
  IMPORT: 'import',
  PARKED: 'parked',
  PENDING: 'pending',
  PARTIAL_ORDER: 'partial_order',
  DELIVERED: 'delivered',
} as const

export type OrderStatus = (typeof ORDER_STATUSES)[keyof typeof ORDER_STATUSES]

// Status metadata for UI display
export const ORDER_STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; color: string }
> = {
  [ORDER_STATUSES.IMPORT]: { label: 'Import', color: 'text-blue-500' },
  [ORDER_STATUSES.PARKED]: { label: 'Parkert', color: 'text-orange-500' },
  [ORDER_STATUSES.PENDING]: { label: 'Klar', color: 'text-green-500' },
  [ORDER_STATUSES.PARTIAL_ORDER]: {
    label: 'Delvis utlevert',
    color: 'text-yellow-500',
  },
  [ORDER_STATUSES.DELIVERED]: { label: 'Utlevert', color: 'text-gray-500' },
} as const

/**
 * Check if an order can be parked
 * Orders with status 'import' or 'parked' can be parked
 * (re-parking adds to parking_history)
 */
export function canParkOrder(status: string): boolean {
  return status === ORDER_STATUSES.IMPORT || status === ORDER_STATUSES.PARKED
}

/**
 * Check if a parked order can be set to ready
 * Only orders with status 'parked' can be set to ready
 */
export function canSetOrderReady(status: string): boolean {
  return status === ORDER_STATUSES.PARKED
}

/**
 * Check if an order can be split
 * Orders with status 'import' or 'partial_order' can be split
 * - import: creates first delivery (Del 1)
 * - partial_order: creates additional deliveries (Del 2, Del 3, etc.)
 */
export function canSplitOrder(status: string): boolean {
  return status === ORDER_STATUSES.IMPORT || status === ORDER_STATUSES.PARTIAL_ORDER
}

/**
 * Get the route path for an order based on its status
 */
export function getOrderRoutePath(orderId: number | string, status: string): string {
  switch (status) {
    case ORDER_STATUSES.IMPORT:
      return `/delivery/${orderId}/import`
    case ORDER_STATUSES.PARKED:
      return `/delivery/${orderId}/parked`
    case ORDER_STATUSES.PENDING:
      return `/delivery/${orderId}/pending`
    case ORDER_STATUSES.PARTIAL_ORDER:
      return `/delivery/${orderId}/partial`
    case ORDER_STATUSES.DELIVERED:
      return `/delivery/${orderId}/delivered`
    default:
      return `/delivery/${orderId}/import`
  }
}
